// Third-party packages
import {
	EnumStatusTheme,
} from '@enums/theme.enum';
import {
	GetCompanyEstablishmentsError,
} from '@exceptions/GetCompanyEstablishmentsError';
import {
	GetCompanyLandsError,
} from '@exceptions/GetCompanyLandsError copy 4';
import {
	GetCompanyManagersError,
} from '@exceptions/GetCompanyManagersError';
import {
	GetCompanyTransactionsError,
} from '@exceptions/GetCompanyTransactionsError';
import {
	handleErrorResponse,
} from '@modules/utils';
import {
	keepPreviousData,
	useQuery,
} from '@tanstack/react-query';
import {
	useEffect,
} from 'react';
import {
	useTranslation,
} from 'react-i18next';
import {
	useDispatch,
	useSelector,
} from 'react-redux';

// Project modules
import {
	getCompanyBuildingList,
	getCompanyEstablishments,
	getCompanyEvents,
	getCompanyLands,
	getCompanyManagers,
	getCompanyTransactions,
	getInformation,
} from '@api/company';
import {
	setCompanyDetails,
	setCompanyError,
	setCompanyLoading,
} from '@stores/_slices/companies';
import {
	setCompanyBuildings,
} from '@stores/_slices/companyBuildings';
import {
	setError, setLoading, setManagers,
} from '@stores/_slices/companyManagers';
import {
	addToastMsg,
} from '@stores/_slices/toast_msgs';
import {
	selectUserToken,
} from '@stores/selectors/userSelectors';

interface UseCompanyDetailsParams {
	accessToken: string;
	companyId: number;
}

/**
 * Custom hook to fetch the details of a company
 *
 * @param params - Parameters for fetching company details
 * @returns Query result with company details
 */
export const useCompanyDetails = (params: UseCompanyDetailsParams) => {
	const {
		companyId,
	} = params;
	const dispatch = useDispatch();

	const accessToken = useSelector(selectUserToken);

	const isEnabled = !!companyId;

	const queryKey = [
		'company-details',
		companyId,
	];

	const query = useQuery({
		enabled: isEnabled,
		placeholderData: keepPreviousData,
		queryFn: async () => {
			const response = await getInformation({
				accessToken,
				companyId,
			});
			return response;
		},
		queryKey,
	});

	useEffect(() => {
		if (query.data) {
			dispatch(setCompanyDetails(query.data));
		}
	}, [
		query.data,
		dispatch,
	]);

	useEffect(() => {
		dispatch(setCompanyError(query.isError));
	}, [
		query.isError,
		dispatch,
	]);

	useEffect(() => {
		dispatch(setCompanyLoading(query.isLoading));
	}, [
		query.isLoading,
		dispatch,
	]);

	return {
		query,
	};
};

export const useCompanyManagers = (params: {
	companyId: number,
	pageNumber: number,
	pageSize: number,
	sortBy: string,
	sortOrder: string,
}) => {
	const {
		companyId,
		pageNumber,
		pageSize,
		sortBy,
		sortOrder,
	} = params;
	const dispatch = useDispatch();

	const accessToken = useSelector(selectUserToken);

	const isEnabled = !!companyId;

	const queryKey = [
		'company-officers',
		companyId,
	];

	const query = useQuery({
		enabled: isEnabled,
		placeholderData: keepPreviousData,
		queryFn: async () => {
			const response = await getCompanyManagers({
				accessToken,
				companyId,
				pageNumber,
				pageSize,
				sortBy,
				sortOrder,
			});

			return response;
		},
		queryKey,
	});

	useEffect(() => {
		if (query.data) {
			dispatch(setManagers({
				companyId,
				result: query.data.result,
				total: query.data.total,
			}));
		}
	}, [
		query.data,
		dispatch,
		companyId,
	]);

	useEffect(() => {
		dispatch(setError({
			companyId,
			isError: query.isError,
		}));
	}, [
		query.isError,
		dispatch,
		companyId,
	]);

	useEffect(() => {
		dispatch(setLoading({
			companyId,
			isLoading: query.isLoading,
		}));
	}, [
		query.isLoading,
		dispatch,
		companyId,
	]);

	return {
		query,
	};
};

export const useCompanyEvents = (params: {
	companyId: number,
	page: number,
	size: number,
	sort: string,
}) => {
	const {
		companyId,
		page,
		size,
		sort,
	} = params;
	const {
		t,
	} = useTranslation();
	const dispatch = useDispatch();

	const isEnabled = !!companyId;

	const queryKey = [
		'company-officers',
		companyId,
	];

	const query = useQuery({
		enabled: isEnabled,
		placeholderData: keepPreviousData,
		queryFn: async () => {
			const response = await getCompanyEvents({
				companyId,
				page,
				size,
				sort,
			});
			handleErrorResponse(response, GetCompanyManagersError, () => {
				dispatch(addToastMsg({
					message: t('status.default'),
					theme: EnumStatusTheme.ERROR,
				}));
			});
			return response;
		},
		queryKey,
	});

	return {
		query,
	};
};

export const useCompanyBuildings = (params: {
	companyId: number,
	pageNumber: number,
	pageSize: number,
	sortBy: string,
	sortOrder: string,
}) => {
	const {
		companyId,
		pageNumber,
		pageSize,
		sortBy,
		sortOrder,
	} = params;
	const dispatch = useDispatch();

	const accessToken = useSelector(selectUserToken);

	const isEnabled = !!companyId;

	const queryKey = [
		'company-officers',
		companyId,
	];

	const query = useQuery({
		enabled: isEnabled,
		placeholderData: keepPreviousData,
		queryFn: async () => {
			const response = await getCompanyBuildingList({
				accessToken,
				companyId,
				pageNumber,
				pageSize,
				sortBy,
				sortOrder,
			});

			return response;
		},
		queryKey,
	});

	useEffect(() => {
		if (query.data) {
			dispatch(setCompanyBuildings({
				companyId,
				result: query.data.result,
			}));
		}
	}, [
		query.data,
		dispatch,
		companyId,
	]);

	useEffect(() => {
		dispatch(setError({
			companyId,
			isError: query.isError,
		}));
	}, [
		query.isError,
		dispatch,
		companyId,
	]);

	useEffect(() => {
		dispatch(setLoading({
			companyId,
			isLoading: query.isLoading,
		}));
	}, [
		query.isLoading,
		dispatch,
		companyId,
	]);

	return {
		query,
	};
};

export const useCompanyLands = (params: {
	companyId: number,
}) => {
	const {
		companyId,
	} = params;
	const {
		t,
	} = useTranslation();
	const dispatch = useDispatch();

	const isEnabled = !!companyId;

	const queryKey = [
		'company-lands',
		companyId,
	];

	const query = useQuery({
		enabled: isEnabled,
		placeholderData: keepPreviousData,
		queryFn: async () => {
			const response = await getCompanyLands({
				companyId,
			});
			handleErrorResponse(response, GetCompanyLandsError, () => {
				dispatch(addToastMsg({
					message: t('status.default'),
					theme: EnumStatusTheme.ERROR,
				}));
			});
			return response;
		},
		queryKey,
	});

	return {
		query,
	};
};

export const useCompanyEstablishments = (params: {
	companyId: number,
}) => {
	const {
		companyId,
	} = params;
	const {
		t,
	} = useTranslation();
	const dispatch = useDispatch();

	const isEnabled = !!companyId;

	const queryKey = [
		'company-sites',
		companyId,
	];

	const query = useQuery({
		enabled: isEnabled,
		placeholderData: keepPreviousData,
		queryFn: async () => {
			const response = await getCompanyEstablishments({
				companyId,
			});
			handleErrorResponse(response, GetCompanyEstablishmentsError, () => {
				dispatch(addToastMsg({
					message: t('status.default'),
					theme: EnumStatusTheme.ERROR,
				}));
			});
			return response;
		},
		queryKey,
	});

	return {
		query,
	};
};

export const useCompanyTransactions = (params: {
	companyId: number,
}) => {
	const {
		companyId,
	} = params;
	const {
		t,
	} = useTranslation();
	const dispatch = useDispatch();

	const isEnabled = !!companyId;

	const queryKey = [
		'company-transactions',
		companyId,
	];

	const query = useQuery({
		enabled: isEnabled,
		placeholderData: keepPreviousData,
		queryFn: async () => {
			const response = await getCompanyTransactions({
				companyId,
			});
			handleErrorResponse(response, GetCompanyTransactionsError, () => {
				dispatch(addToastMsg({
					message: t('status.default'),
					theme: EnumStatusTheme.ERROR,
				}));
			});
			return response;
		},
		queryKey,
	});

	return {
		query,
	};
};